import { useEffect, useState } from "react";
import { Download, Plus } from "lucide-react";
import api, { downloadFile, apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function AdminAgencyFees() {
  const [config, setConfig] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [adj, setAdj] = useState({ agency_cut: "", note: "" });

  const load = () => {
    api.get("/admin/agency-fees/config").then(({ data }) => setConfig(data));
    api.get("/admin/agency-fees/ledger").then(({ data }) => setLedger(data));
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try { await api.put("/admin/agency-fees/config", { mode: config.mode, percentage: parseFloat(config.percentage || 0), flat_per_item: parseFloat(config.flat_per_item || 0) }); toast.success("Fee structure saved."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addAdj = async () => {
    if (!adj.agency_cut) return;
    try { await api.post("/admin/agency-fees/adjust", { agency_cut: parseFloat(adj.agency_cut), note: adj.note }); setAdj({ agency_cut: "", note: "" }); load(); toast.success("Adjustment logged."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const exportCsv = () => downloadFile("/admin/agency-fees/export", "agency_ledger.csv").catch(() => toast.error("Export failed."));

  if (!config || !ledger) return <p className="font-heading text-2xl text-ink/40">Loading…</p>;
  const S = ledger.summary;

  return (
    <div data-testid="admin-agency-fees">
      <h1 className="font-heading text-4xl tracking-tight mb-2">Agency Fees & Revenue Ledger</h1>
      <p className="font-body text-sm text-ink/60 mb-8">Configure your platform fee per sale and track the revenue split.</p>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="adm-card">
          <h3 className="font-heading text-xl mb-4">Fee Structure</h3>
          <label className="adm-label">Fee Mode</label>
          <select className="adm-input mb-4" value={config.mode} onChange={(e) => setConfig({ ...config, mode: e.target.value })} data-testid="fee-mode-select">
            <option value="percentage">Percentage of sale</option>
            <option value="flat">Flat fee per item</option>
            <option value="hybrid">Hybrid (percentage + per item)</option>
          </select>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="adm-label">Percentage %</label><input className="adm-input" type="number" value={config.percentage} onChange={(e) => setConfig({ ...config, percentage: e.target.value })} data-testid="fee-percentage-input" /></div>
            <div><label className="adm-label">Flat / item $</label><input className="adm-input" type="number" value={config.flat_per_item} onChange={(e) => setConfig({ ...config, flat_per_item: e.target.value })} /></div>
          </div>
          <button onClick={saveConfig} className="adm-btn mt-4 w-full" data-testid="fee-save-btn">Save Fee Structure</button>
        </div>

        <div className="adm-card">
          <h3 className="font-heading text-xl mb-4">Manual Adjustment</h3>
          <label className="adm-label">Adjustment Amount $</label>
          <input className="adm-input mb-3" type="number" value={adj.agency_cut} onChange={(e) => setAdj({ ...adj, agency_cut: e.target.value })} data-testid="adjust-amount-input" />
          <label className="adm-label">Note</label>
          <input className="adm-input mb-4" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} />
          <button onClick={addAdj} className="adm-btn-outline w-full" data-testid="adjust-add-btn"><Plus size={13} /> Log Adjustment</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[["Today", S.day], ["This Week", S.week], ["This Month", S.month], ["All Time", S.all_time]].map(([l, s], i) => (
          <div key={i} className="adm-card" data-testid={`fee-summary-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="adm-label">{l}</p>
            <p className="font-heading text-3xl">${s.agency_cut.toFixed(2)}</p>
            <p className="font-body text-[0.6rem] text-ink/50 mt-1">on ${s.gross.toFixed(2)} · {s.count} sale(s)</p>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading text-2xl">Ledger</h3>
        <button onClick={exportCsv} className="adm-btn-outline" data-testid="export-ledger-btn"><Download size={13} /> Export CSV</button>
      </div>
      <div className="adm-card p-0 overflow-hidden">
        <table className="w-full text-left font-body text-sm">
          <thead className="bg-cream"><tr className="uppercase tracking-wide2 text-[0.6rem] text-ink/60"><th className="px-5 py-3">Date</th><th className="px-5 py-3">Order</th><th className="px-5 py-3">Gross</th><th className="px-5 py-3">Agency Cut</th><th className="px-5 py-3">Net Payout</th></tr></thead>
          <tbody>
            {ledger.entries.map((e) => (
              <tr key={e.id} className="border-t border-ink/5">
                <td className="px-5 py-3 text-ink/60">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3">{e.order_number}</td>
                <td className="px-5 py-3">${e.gross.toFixed(2)}</td>
                <td className="px-5 py-3 font-medium">${e.agency_cut.toFixed(2)}</td>
                <td className="px-5 py-3">${e.net_payout.toFixed(2)}</td>
              </tr>
            ))}
            {ledger.entries.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-ink/40">No ledger entries yet — they appear after paid orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
